/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { SubtopicPaging, TopicEntry, TopicPaging } from '../model';
import { BaseApi } from './base.api';
import { buildCollectionParam } from '../../../alfrescoApiClient';
import { throwIfNotDefined } from '../../../assert';
export class ClassificationGuidesApi extends BaseApi {
    combinedInstructions(opts) {
        return this.post({
            path: '/combined-instructions',
            bodyParam: opts === null || opts === void 0 ? void 0 : opts.instructions
        });
    }
    createClassificationGuide(classificationGuide) {
        throwIfNotDefined(classificationGuide, 'classificationGuide');
        return this.post({
            path: '/classification-guides',
            bodyParam: classificationGuide
        });
    }
    createSubtopic(topicId, topic, opts) {
        throwIfNotDefined(topicId, 'topicId');
        throwIfNotDefined(topic, 'topic');
        const pathParams = {
            topicId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.post({
            path: '/topics/{topicId}/subtopics',
            pathParams,
            queryParams,
            bodyParam: topic,
            returnType: TopicEntry
        });
    }
    createTopic(classificationGuideId, topic, opts) {
        throwIfNotDefined(classificationGuideId, 'classificationGuideId');
        throwIfNotDefined(topic, 'topic');
        const pathParams = {
            classificationGuideId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.post({
            path: '/classification-guides/{classificationGuideId}/topics',
            pathParams,
            queryParams,
            bodyParam: topic,
            returnType: TopicEntry
        });
    }
    deleteClassificationGuide(classificationGuideId) {
        throwIfNotDefined(classificationGuideId, 'classificationGuideId');
        const pathParams = {
            classificationGuideId
        };
        return this.delete({
            path: '/classification-guides/{classificationGuideId}',
            pathParams
        });
    }
    deleteTopic(topicId) {
        throwIfNotDefined(topicId, 'topicId');
        const pathParams = {
            topicId
        };
        return this.delete({
            path: '/topics/{topicId}',
            pathParams
        });
    }
    listClassificationGuides(opts) {
        opts = opts || {};
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where
        };
        return this.get({
            path: '/classification-guides',
            queryParams
        });
    }
    listSubtopics(topicId, opts) {
        throwIfNotDefined(topicId, 'topicId');
        opts = opts || {};
        const pathParams = {
            topicId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            includeSource: opts === null || opts === void 0 ? void 0 : opts.includeSource
        };
        return this.get({
            path: '/topics/{topicId}/subtopics',
            pathParams,
            queryParams,
            returnType: SubtopicPaging
        });
    }
    listTopics(classificationGuideId, opts) {
        throwIfNotDefined(classificationGuideId, 'classificationGuideId');
        opts = opts || {};
        const pathParams = {
            classificationGuideId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            includeSource: opts === null || opts === void 0 ? void 0 : opts.includeSource
        };
        return this.get({
            path: '/classification-guides/{classificationGuideId}/topics',
            pathParams,
            queryParams,
            returnType: TopicPaging
        });
    }
    showClassificationGuideById(classificationGuideId) {
        throwIfNotDefined(classificationGuideId, 'classificationGuideId');
        const pathParams = {
            classificationGuideId
        };
        return this.get({
            path: '/classification-guides/{classificationGuideId}',
            pathParams
        });
    }
    showTopicById(topicId, opts) {
        throwIfNotDefined(topicId, 'topicId');
        const pathParams = {
            topicId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.post({
            path: '/topics/{topicId}',
            pathParams,
            queryParams,
            returnType: TopicEntry
        });
    }
    updateClassificationGuide(classificationGuideId, classificationGuide) {
        throwIfNotDefined(classificationGuideId, 'classificationGuideId');
        throwIfNotDefined(classificationGuide, 'classificationGuide');
        const pathParams = {
            classificationGuideId
        };
        return this.put({
            path: '/classification-guides/{classificationGuideId}',
            pathParams,
            bodyParam: classificationGuide
        });
    }
    updateTopic(topicId, topic, opts) {
        throwIfNotDefined(topicId, 'topicId');
        throwIfNotDefined(topic, 'topic');
        const pathParams = {
            topicId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.put({
            path: '/topics/{topicId}',
            pathParams,
            queryParams,
            bodyParam: topic,
            returnType: TopicEntry
        });
    }
}
